import type { ListingPage } from "@wags/catalog/listings/listing-page.contract.ts";
import { formatMoneyRange, type MoneyRange } from "@/lib/ui/price";
import type { ListingData } from "./listing-section-data";
import { mergeListingSizeFacts } from "./listing-size";
import {
    MODE_ORDER,
    type TransportMode,
    type TransportModeKind,
    type TransportModel,
} from "./transport";

type TransportOption = NonNullable<ListingPage["transport"]>["options"][number];

const MODE_KIND_BY_OPTION_TYPE: Record<
    TransportOption["type"],
    TransportModeKind | null
> = {
    pickup: "pickup",
    meetup: "pickup",
    ground_delivery: "ground",
    flight_nanny: "flight_nanny",
    air_cargo: "air_cargo",
    other: null,
};

const MODE_LABEL: Record<TransportModeKind, string> = {
    pickup: "Pickup",
    ground: "Ground delivery",
    flight_nanny: "Flight nanny",
    air_cargo: "Air cargo",
};

const STATE_CODE_PATTERN = /^[A-Z]{2}$/;

function destinationStates(option: TransportOption): string[] {
    const codes = option.locations
        .filter((location) => location.countryCode === "US")
        .map((location) => location.regionCode?.trim().toUpperCase())
        .filter(
            (region): region is string =>
                Boolean(region) && STATE_CODE_PATTERN.test(region as string),
        );
    return [...new Set(codes)];
}

function pickupSummary(option: TransportOption): string | undefined {
    const location = option.locations[0];
    if (!location) return undefined;
    const place =
        location.city && location.regionCode
            ? `${location.city}, ${location.regionCode}`
            : (location.city ?? location.regionCode ?? location.displayName);
    return place?.trim() || undefined;
}

function meetupLabels(kindOptions: TransportOption[]): string[] | undefined {
    const cities = kindOptions
        .flatMap((option) =>
            option.locations
                .filter(
                    (location) =>
                        option.type === "meetup" ||
                        location.kind === "meetup_point",
                )
                .map((location) =>
                    location.city && location.regionCode
                        ? `${location.city}, ${location.regionCode}`
                        : (location.city ??
                          location.regionCode ??
                          location.displayName),
                ),
        )
        .map((city) => city.trim())
        .filter((city) => city.length > 0);
    const uniqueCities = [...new Set(cities)];
    return uniqueCities.length > 0 ? uniqueCities : undefined;
}

function collapsedCostRange(kindOptions: TransportOption[]): MoneyRange | null {
    const costs = kindOptions
        .map((option) => option.cost)
        .filter((cost): cost is NonNullable<typeof cost> => cost != null);
    const currencies = new Set(costs.map((cost) => cost.currency));
    const priced = currencies.size === 1 ? costs : [];
    const currency = priced[0]?.currency ?? "USD";
    const mins = priced
        .map((cost) => cost.min)
        .filter((value): value is number => value != null);
    const maxes = priced
        .map((cost) => cost.max)
        .filter((value): value is number => value != null);
    if (mins.length > 0) {
        return { currency, min: Math.min(...mins), max: null };
    }
    if (maxes.length > 0) {
        return { currency, min: null, max: Math.max(...maxes) };
    }
    return null;
}

function feeLabels(range: MoneyRange | null) {
    const standaloneFee = formatMoneyRange(range);
    return {
        standaloneFee,
        feeLabel: standaloneFee
            ? standaloneFee.charAt(0).toLowerCase() + standaloneFee.slice(1)
            : undefined,
    };
}

function pickupMode(kindOptions: TransportOption[]): TransportMode {
    const city = kindOptions.map(pickupSummary).find(Boolean) ?? undefined;
    const hasPickup = kindOptions.some((option) => option.type === "pickup");
    const hasMeetup = kindOptions.some((option) => option.type === "meetup");
    const meetups = hasMeetup ? meetupLabels(kindOptions) : undefined;
    const label = hasPickup
        ? hasMeetup
            ? "Pickup or meetup"
            : "Pickup"
        : "Meetup";
    return {
        kind: "pickup",
        label,
        summary: city ?? "Available",
        available: true,
        detail: city
            ? {
                  kind: "pickup",
                  city,
                  meetsHalfway: hasMeetup,
                  meetups,
              }
            : undefined,
    };
}

function groundMode(
    kindOptions: TransportOption[],
    seekerState: string | null | undefined,
    feeLabel: string | undefined,
): TransportMode {
    const states = [...new Set(kindOptions.flatMap(destinationStates))];
    const coversSeeker = Boolean(seekerState && states.includes(seekerState));
    const reach = coversSeeker
        ? `Reaches ${seekerState}`
        : states.length > 0
          ? `Reaches ${states.length} ${states.length === 1 ? "state" : "states"}`
          : "Delivered to you";
    return {
        kind: "ground",
        label: MODE_LABEL.ground,
        summary: [reach, feeLabel].filter(Boolean).join(" · "),
        available: true,
        detail: {
            kind: "ground",
            states,
            feeLabel,
            coversSeeker,
            seekerState: coversSeeker ? (seekerState ?? null) : null,
        },
    };
}

function modesFromOptions(
    options: TransportOption[],
    seekerState: string | null | undefined,
): Map<TransportModeKind, TransportMode> {
    const byKind = new Map<TransportModeKind, TransportOption[]>();
    for (const option of options) {
        const kind = MODE_KIND_BY_OPTION_TYPE[option.type];
        if (!kind) continue;
        const forKind = byKind.get(kind);
        if (forKind) forKind.push(option);
        else byKind.set(kind, [option]);
    }

    const modes = new Map<TransportModeKind, TransportMode>();
    for (const [kind, kindOptions] of byKind) {
        const { standaloneFee, feeLabel } = feeLabels(
            collapsedCostRange(kindOptions),
        );

        if (kind === "pickup") {
            modes.set(kind, pickupMode(kindOptions));
            continue;
        }
        if (kind === "ground") {
            modes.set(kind, groundMode(kindOptions, seekerState, feeLabel));
            continue;
        }
        modes.set(kind, {
            kind,
            label: MODE_LABEL[kind],
            summary: standaloneFee ?? "Available",
            available: true,
            detail:
                kind === "flight_nanny" ? { kind: "flight_nanny" } : undefined,
        });
    }
    return modes;
}

function isSubstantive(mode: TransportMode): boolean {
    const detail = mode.detail;
    if (!detail) return false;
    if (detail.kind === "ground")
        return detail.states.length > 0 || Boolean(detail.feeLabel);
    if (detail.kind === "pickup") return Boolean(detail.city);
    return true;
}

export function mergeTransportModel(
    batch: TransportModel | undefined,
    options: TransportOption[] | undefined,
    seekerState: string | null | undefined,
): TransportModel | undefined {
    if (!options || options.length === 0) return batch;
    const fromEndpoint = modesFromOptions(options, seekerState);
    const batchByKind = new Map(
        (batch?.modes ?? []).map((mode) => [mode.kind, mode]),
    );

    const modes = MODE_ORDER.map((kind) => {
        const endpointMode = fromEndpoint.get(kind);
        const batchMode = batchByKind.get(kind);
        if (endpointMode && batchMode) {
            if (!isSubstantive(endpointMode) && isSubstantive(batchMode))
                return batchMode;
            return endpointMode;
        }
        return endpointMode ?? batchMode;
    }).filter((mode): mode is TransportMode => mode !== undefined);

    return { modes, hasAny: modes.some((mode) => mode.available) };
}

const GETS_ALONG_LABEL: Record<
    ListingPage["listing"]["facts"]["getsAlongWith"][number]["type"],
    string
> = {
    children: "Kids",
    dogs: "Dogs",
    cats: "Cats",
    other_animals: "Other animals",
};

function goodWithFromFacts(page: ListingPage): string[] | undefined {
    const facts = page.listing.facts.getsAlongWith;
    if (facts.length === 0) return undefined;
    return facts
        .filter((entry) => entry.value === "yes")
        .map((entry) => GETS_ALONG_LABEL[entry.type]);
}

function transportRows(
    options: TransportOption[],
): ListingData["transports"] | undefined {
    const rows = options.map((option) => {
        const kind = MODE_KIND_BY_OPTION_TYPE[option.type];

        const states = kind === "pickup" ? [] : destinationStates(option);
        const reach =
            states.length > 0
                ? `Reaches ${states.length} ${states.length === 1 ? "state" : "states"}`
                : kind === "pickup"
                  ? pickupSummary(option)
                  : undefined;
        return {
            label:
                option.type === "meetup"
                    ? "Meetup"
                    : kind
                      ? MODE_LABEL[kind]
                      : "Transport",
            sub: option.details?.trim() || reach,
            price: formatMoneyRange(option.cost),
        };
    });
    return rows.length > 0 ? rows : undefined;
}

function preferRicherRows(
    endpoint: ListingData["transports"] | undefined,
    batch: ListingData["transports"] | undefined,
): ListingData["transports"] | undefined {
    if (!endpoint) return batch;
    const saysSomething = endpoint.some((row) => row.sub || row.price);
    if (!saysSomething && batch?.some(batchRowSaysSomething)) return batch;
    return endpoint;
}

const GENERIC_TRANSPORT_LABELS = new Set<string>([
    ...Object.values(MODE_LABEL),
    "Pickup available",
    "Delivery available",
]);

function batchRowSaysSomething(row: {
    label: string;
    sub?: string;
    price?: string;
}): boolean {
    if (row.sub || row.price) return true;
    return !GENERIC_TRANSPORT_LABELS.has(row.label.trim());
}

function fallbackMapped<T>(
    items: readonly T[] | undefined,
    map: (item: T) => string,
    fallback: string[] | undefined,
) {
    if (!items || items.length === 0) return fallback;
    return items.map(map);
}

function titleCase(value: string | null | undefined) {
    if (!value) return undefined;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function endpointSexLabel(sex: ListingPage["listing"]["facts"]["sex"]) {
    if (sex === "unknown") return undefined;
    return titleCase(sex);
}

function mergeListingDescription(base: ListingData, page: ListingPage) {
    return {
        description: page.listing.description?.trim() || base.description,
        descriptionQa: page.listing.descriptionQa ?? base.descriptionQa,
    };
}

export function mergeListingPage(
    base: ListingData | null,
    page: ListingPage | null,
    seekerState?: string | null,
): ListingData | null {
    if (!base || !page) return base;
    const options = page.transport?.options ?? [];
    const health = page.listing.healthAndWellness;
    const endpointSize = page.listing.facts.size?.trim();

    return {
        ...base,

        ...mergeListingDescription(base, page),
        priceLabel: formatMoneyRange(page.listing.price) ?? base.priceLabel,

        ageLabel: base.ageLabel ?? page.listing.facts.age?.trim() ?? undefined,
        sexLabel: base.sexLabel ?? endpointSexLabel(page.listing.facts.sex),
        ...mergeListingSizeFacts(base, titleCase(endpointSize)),

        avgPrice: formatMoneyRange(page.breed?.estimatedPrice) ?? base.avgPrice,
        goodWith: goodWithFromFacts(page) ?? base.goodWith,
        healthTests: fallbackMapped(
            health?.tests,
            (test) =>
                test.result ? `${test.name} — ${test.result}` : test.name,
            base.healthTests,
        ),
        reportedHealth: fallbackMapped(
            health?.reportedHealth,
            (item) => item.name,
            base.reportedHealth,
        ),
        completedCare: fallbackMapped(
            health?.completedCare,
            (item) => item.name,
            base.completedCare,
        ),
        transports: preferRicherRows(transportRows(options), base.transports),
        transportModel: mergeTransportModel(
            base.transportModel,
            options,
            seekerState,
        ),
    };
}
